import React, { FC, ReactElement, useMemo } from 'react';
import { Button, Chip, type SxProps, type Theme, useTheme } from '@mui/material';
import clsx from 'clsx';
import { type TFunction } from 'i18next';
import { mergeSx } from 'merge-sx';

import { borders } from '@/theme/constants';
import typography from '@/theme/typography';

export interface IndicatorChipProps {
  sx?: SxProps<Theme>;
  label: string | ReactElement;
  onClick?: <EventType>(event: EventType) => void;
  icon?: () => ReactElement;
  isSelected?: boolean;
  className?: string;
}

export interface IndicatorChipVariant {
  label: (t: TFunction) => string | ReactElement;
  value: string | number | null;
  sx?: SxProps<Theme>;
  Icon?: () => ReactElement;
}

export type IndicatorChipVariantTranslated = Omit<IndicatorChipVariant, 'label'> & {
  label: string | ReactElement;
};

const IndicatorChip: FC<IndicatorChipProps> = ({ sx, label, onClick, icon, isSelected, className }) => {
  const { spacing } = useTheme();

  const Icon = useMemo(() => icon?.(), [icon]);

  return (
    <Chip
      component={Button}
      sx={mergeSx<Theme>(
        {
          minWidth: 'unset',
          height: spacing(3.5),
          borderRadius: spacing(0.5),
          border: borders.thin,
          borderColor: 'grey.400',
          color: 'grey.800',
          bgcolor: 'background.default',
          fontWeight: typography.fontWeightRegular,
          transitionProperty: 'background-color, box-shadow, border',
          '&.Mui-selected': {
            borderColor: 'primary.main',
            color: 'primary.main'
          },
          '&.IndicatorChip-clickable:hover': {
            bgcolor: 'primary.200',
            borderColor: 'primary.200'
          },
          '&.IndicatorChip-unclickable:hover': {
            bgcolor: 'transparent'
          },
          '&.Mui-selected.IndicatorChip-clickable:hover': {
            borderColor: 'primary.main'
          },
          '& .MuiChip-label': {
            p: spacing(0, 2),
            ...typography.body3
          }
        },
        sx
      )}
      label={label}
      onClick={onClick}
      icon={Icon}
      className={clsx('IndicatorChip-root', className, {
        'Mui-selected': isSelected,
        'IndicatorChip-clickable': !!onClick,
        'IndicatorChip-unclickable': !onClick
      })}
      disableRipple={!onClick}
    />
  );
};

export default IndicatorChip;
